import { z } from "zod";

/**
 * Von Hand erfasste Abwesenheiten — Schemas für API und Web.
 *
 * **Wozu es das gibt.** Der Statusbericht sagt dem Kunden, wann er uns nicht
 * erreicht. Personio liefert dafür nur Urlaub und Krankheit — Stundenabbau,
 * Workshops und Fortbildungen stehen dort gar nicht, müssen aber in den
 * Bericht. Hier werden sie eingetragen.
 *
 * **Was NICHT erfasst wird, ist der Grund.** Nicht einmal als Auswahlliste:
 * Im Bericht steht ohnehin nur „abwesend von–bis", und eine Liste kennt nur,
 * was jemand hineingeschrieben hat. Die erste Fassung hatte eine
 * (Betriebsferien, Fortbildung, …) und war damit sofort unvollständig, während
 * sie gleichzeitig ein Feld anbot, in dem irgendwann ein Krankheitsgrund
 * stünde — ein Gesundheitsdatum nach Art. 9 DSGVO (Entscheidung 03.08.2026,
 * s. `PERSONIO_NEUTRAL_ABSENCE_TYPE`).
 */

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/**
 * Obergrenze eines Zeitraums. Ein Jahr und ein Stück deckt jede
 * Betriebsferien- und Sonderurlaubslage ab; ein Tippfehler im Jahr (2026 statt
 * 2027) läuft damit in eine Fehlermeldung statt in zehntausend Tageszeilen.
 */
export const MANUAL_ABSENCE_MAX_DAYS = 400;

/** Erlaubte Tagesanteile — ganzer oder halber Tag, nichts dazwischen. */
export const manualAbsenceDayFractionSchema = z.union([
  z.literal(1),
  z.literal(0.5),
]);

export const manualAbsenceSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema,
  dayFraction: manualAbsenceDayFractionSchema,
  /** Kalendertage des Zeitraums (Wochenenden mitgezählt, wie bei Personio). */
  days: z.number().int().positive(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type ManualAbsence = z.infer<typeof manualAbsenceSchema>;

const zeitraumFelder = {
  dateFrom: isoDateSchema,
  dateTo: isoDateSchema,
  dayFraction: manualAbsenceDayFractionSchema.default(1),
};

/** `dateTo` darf nicht vor `dateFrom` liegen und der Zeitraum nicht ausufern. */
function pruefeZeitraum(
  wert: { dateFrom: string; dateTo: string },
  ctx: z.RefinementCtx,
): void {
  if (wert.dateTo < wert.dateFrom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["dateTo"],
      message: "dateTo liegt vor dateFrom",
    });
    return;
  }
  const tage =
    Math.round(
      (Date.parse(`${wert.dateTo}T00:00:00Z`) -
        Date.parse(`${wert.dateFrom}T00:00:00Z`)) /
        86_400_000,
    ) + 1;
  if (tage > MANUAL_ABSENCE_MAX_DAYS) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["dateTo"],
      message: `Zeitraum umfasst ${tage} Tage, erlaubt sind ${MANUAL_ABSENCE_MAX_DAYS}`,
    });
  }
}

/**
 * Anlegen — für MEHRERE Personen in einem Zug.
 *
 * Betriebsferien betreffen alle, und wer sie vierzig Mal von Hand eintragen
 * muss, trägt sie nicht ein. Es entsteht trotzdem **eine Zeile je Person**,
 * kein „gilt für alle"-Eintrag: Nur so lässt sich hinterher eine einzelne
 * davon ändern oder löschen, und nur so muss kein Leser eine zweite Bedeutung
 * kennen.
 */
export const createManualAbsenceFieldsSchema = z.object({
  employeeIds: z.array(z.string().uuid()).min(1).max(200),
  ...zeitraumFelder,
});
export const createManualAbsenceSchema =
  createManualAbsenceFieldsSchema.superRefine(pruefeZeitraum);
export type CreateManualAbsenceInput = z.infer<
  typeof createManualAbsenceSchema
>;

/** Ändern — die Person bleibt, der Zeitraum darf sich verschieben. */
export const updateManualAbsenceFieldsSchema = z.object(zeitraumFelder);
export const updateManualAbsenceSchema =
  updateManualAbsenceFieldsSchema.superRefine(pruefeZeitraum);
export type UpdateManualAbsenceInput = z.infer<
  typeof updateManualAbsenceSchema
>;

export const manualAbsenceQuerySchema = z.object({
  employeeId: z.string().uuid().optional(),
  segmentId: z.string().uuid().optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});
export type ManualAbsenceQuery = z.infer<typeof manualAbsenceQuerySchema>;

export const manualAbsenceListSchema = z.object({
  items: z.array(manualAbsenceSchema),
});
export type ManualAbsenceList = z.infer<typeof manualAbsenceListSchema>;
